import Box from '@/common/components/Box';
import { useGovernmentAllocationsDistributions } from '@/features/governmentAllocations/hooks/useGovernmentAllocationsDistributions';
import { useParams } from 'react-router-dom';
import GovernmentAllocationDistributionBranchItem from './GovernmentAllocationDistributionBranchItem';
import { CompanyGovernmentAllocationDistribution } from '@/features/governmentAllocations/types/governmentAllocationDistribution.type';
import { Mapper } from '@/common/services/mapper';
import { CompanyBranchData } from '@/common/types/companyBranches.type';
import { InfoIcon, SearchIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import Select from '@/common/components/Select';
import GovernmentAllocationDistributionCreateModal from '@/features/governmentAllocations/components/GovernmentAllocationDistribution/CreateDistributionModal';
import { useGovernmentAllocationsBranchesToDistribute } from '@/features/governmentAllocations/hooks/useGovernmentAllocationsBranchesToDistribute';
import TextField from '@/common/components/TextField';
import useDebounce from '@/common/hooks/useDebounce';

interface GovernmentAllocationDistributionProps {
  isConsumptionControlledByQuantity?: boolean;
}

export const GovernmentAllocationDistribution = ({
  isConsumptionControlledByQuantity,
}: GovernmentAllocationDistributionProps) => {
  const { id } = useParams();

  const governmentAllocationDistributionsContainerRef = useRef<HTMLDivElement>(null);

  const [companyBranch, setCompanyBranch] = useState<CompanyBranchData>();
  const [searchBranch, setSearchBranch] = useDebounce('');
  const [searchGovernmentAllocationsDistributions, setSearchGovernmentAllocationsDistributions] = useDebounce('');

  const { data: branchesToDistributeOptions, meta: branchesToDistributeMeta } = useGovernmentAllocationsBranchesToDistribute({
    filters: {
      governmentAllocationId: id,
      search: searchBranch,
      pageSize: 9999,
    },
    mapper: Mapper.mapToOptionsWithItem<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' }),
  });

  const {
    data: governmentAllocationDistributions,
    meta: governmentAllocationDistributionsMeta,
    isFetching: isFetchingGovernmentAllocationDistributions,
    fetchNextPage: fetchGovernmentAllocationDistributionsNextPage,
  } = useGovernmentAllocationsDistributions<CompanyGovernmentAllocationDistribution[]>({
    governmentAllocationId: id,
    filters: {
      search: searchGovernmentAllocationsDistributions,
    },
  });

  const shouldFetchNextPage = () => {
    if (
      !governmentAllocationDistributionsContainerRef.current ||
      governmentAllocationDistributionsMeta.current_page >= governmentAllocationDistributionsMeta.total_pages ||
      isFetchingGovernmentAllocationDistributions
    ) {
      return false;
    }

    const { scrollTop, scrollHeight, clientHeight } = governmentAllocationDistributionsContainerRef.current;
    return scrollTop + clientHeight >= scrollHeight - 16;
  };

  const handleScroll = () => {
    const hasNextPage = shouldFetchNextPage();

    if (hasNextPage) {
      fetchGovernmentAllocationDistributionsNextPage();
    }
  };

  return (
    <>
      <Box className="flex flex-col gap-4">
        <p>Divisão de crédito do empenho</p>

        <div className="flex gap-x-2 items-center">
          <InfoIcon strokeWidth={1} className="text-yellow-600" />

          <div className="flex flex-col gap-1">
            <p className="font-bold text-xs leading-3">
              Você poderá dividir o {isConsumptionControlledByQuantity ? 'quantidade empenhada' : 'valor empenhado'} para cada Departamento associado incluindo essa filial.
            </p>
            <p className="font-light text-[10px] italic leading-3">
              Caso um departamento não seja adicionado aqui, o mesmo não terá crédito.
            </p>
          </div>
        </div>

        <Select
          placeholder={
            <div className="flex items-center gap-2">
              <SearchIcon strokeWidth={1} />
              <p>Pesquisar departamentos</p>
            </div>
          }
          onInputChange={setSearchBranch}
          options={branchesToDistributeOptions?.filter((branchOption) => {
            return !governmentAllocationDistributions?.some(({ branch }) => branch.id === branchOption.value);
          }) || []}
          value={''}
          onChange={(newValue) => setCompanyBranch((newValue as { item: CompanyBranchData; }).item)}
        />

        <p className="text-sm">
          {governmentAllocationDistributionsMeta.total_distributions_count}/
          {governmentAllocationDistributionsMeta.total_distributions_count + branchesToDistributeMeta.total}{' '}
          Departamentos adicionados
        </p>

        <Box className="flex flex-col gap-y-4 p-4">
          <TextField
            LeftIcon={<SearchIcon strokeWidth={1} />}
            placeholder="Pesquisar"
            onChange={(e) => {
              setSearchGovernmentAllocationsDistributions((e.target as HTMLInputElement).value);
            }}
          />

          {!!governmentAllocationDistributions?.length && (
            <div
              ref={governmentAllocationDistributionsContainerRef}
              onScroll={handleScroll}
              className="flex max-h-[calc(100vh-496px)] flex-col gap-4 overflow-y-scroll pr-2"
            >
              {governmentAllocationDistributions?.map((distribution) => (
                <GovernmentAllocationDistributionBranchItem
                  key={distribution.id}
                  distribution={distribution}
                  isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
                />
              ))}
            </div>
          )}
        </Box>
      </Box>

      <GovernmentAllocationDistributionCreateModal
        companyBranch={companyBranch}
        setCompanyBranch={setCompanyBranch}
        governmentAllocationId={id}
        distribution={governmentAllocationDistributions?.find((distribution) => distribution.branch.id === companyBranch?.id)!}
        isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
      />
    </>
  );
};
